import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, Trophy, Gamepad2, BarChart3, Settings, Bell, FileText, 
  Shield, Activity, TrendingUp, Calendar, UserPlus, Loader2,
  AlertTriangle, ChevronRight, Server, Home
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

const COLORS = ['#C6A87C', '#4DFFFF', '#F61225', '#22C55E', '#9333EA', '#F97316'];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await adminApi.getDashboard();
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError("Bu sayfaya erişim yetkiniz yok.");
      } else {
        setError("Veriler yüklenirken hata oluştu.");
      }
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-unauthorized">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
          <p className="text-[#B1C1C1]">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
          <Link to="/dashboard" className="text-[#4DFFFF] hover:underline mt-4 inline-block">
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Hata</h1>
          <p className="text-[#B1C1C1]">{error}</p>
        </div>
      </div>
    );
  }

  const { summary, charts } = data || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A87C] to-[#8B6914] flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Admin Panel
            </h1>
            <p className="text-sm text-[#64748B]">Kahve LİGİ Yönetim Merkezi</p>
          </div>
        </div>
        
        {/* Quick Links */}
        <div className="flex gap-2">
          <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
            <Home className="w-5 h-5" />
          </Link>
          <Link to="/admin/system-status" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Sistem Durumu">
            <Server className="w-5 h-5" />
          </Link>
          <Link to="/admin/users" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Kullanıcılar">
            <Users className="w-5 h-5" />
          </Link>
          <Link to="/admin/leagues" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Ligler">
            <Trophy className="w-5 h-5" />
          </Link>
          <Link to="/admin/settings" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Ayarlar">
            <Settings className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-[#4DFFFF]" />
            <span className="text-xs text-green-400">+{summary?.today_registrations || 0} bugün</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary?.total_users || 0}</p>
          <p className="text-xs text-[#64748B]">Toplam Üye</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-[#22C55E]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.active_users || 0}</p>
          <p className="text-xs text-[#64748B]">Aktif (7 gün)</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-5 h-5 text-[#C6A87C]" />
            <span className="text-xs text-green-400">+{summary?.today_leagues || 0} bugün</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary?.total_leagues || 0}</p>
          <p className="text-xs text-[#64748B]">Toplam Lig</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Gamepad2 className="w-5 h-5 text-[#F97316]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.total_games_played || 0}</p>
          <p className="text-xs text-[#64748B]">Oynanan Oyun</p>
        </div>
      </div>

      {/* Second Row Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-5 h-5 text-[#9333EA]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.total_tournaments || 0}</p>
          <p className="text-xs text-[#64748B]">Toplam Turnuva</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-[#4DFFFF]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.active_tournaments || 0}</p>
          <p className="text-xs text-[#64748B]">Aktif Turnuva</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <UserPlus className="w-5 h-5 text-[#22C55E]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.today_registrations || 0}</p>
          <p className="text-xs text-[#64748B]">Bugün Kayıt</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-5 h-5 text-[#C6A87C]" />
          </div>
          <p className="text-2xl font-bold text-white">{summary?.today_leagues || 0}</p>
          <p className="text-xs text-[#64748B]">Bugün Lig</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Registration Chart */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            Günlük Kayıtlar (30 Gün)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.daily_registrations || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B" 
                  tick={{ fill: '#64748B', fontSize: 10 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0A1635', border: '1px solid #1E3A5F', borderRadius: '8px' }}
                  labelStyle={{ color: '#B1C1C1' }}
                />
                <Line type="monotone" dataKey="count" stroke="#4DFFFF" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leagues Chart */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            Günlük Ligler (30 Gün)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.daily_leagues || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B" 
                  tick={{ fill: '#64748B', fontSize: 10 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0A1635', border: '1px solid #1E3A5F', borderRadius: '8px' }}
                  labelStyle={{ color: '#B1C1C1' }}
                />
                <Bar dataKey="count" fill="#C6A87C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Game Popularity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="glass-card p-6">
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            En Çok Oynanan Oyunlar
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={charts?.game_popularity || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="_id"
                  label={({ _id, count }) => `${_id}: ${count}`}
                >
                  {(charts?.game_popularity || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0A1635', border: '1px solid #1E3A5F', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            Hızlı Erişim
          </h3>
          <div className="space-y-2">
            {[
              { to: "/admin/system-status", icon: Server, label: "Sistem Durumu", color: "#22C55E" },
              { to: "/admin/users", icon: Users, label: "Kullanıcı Yönetimi", color: "#4DFFFF" },
              { to: "/admin/leagues", icon: Trophy, label: "Lig Yönetimi", color: "#C6A87C" },
              { to: "/admin/games", icon: Gamepad2, label: "Oyun Yönetimi", color: "#F97316" },
              { to: "/admin/announcements", icon: Bell, label: "Duyurular", color: "#22C55E" },
              { to: "/admin/logs", icon: FileText, label: "Sistem Logları", color: "#9333EA" },
              { to: "/admin/settings", icon: Settings, label: "Sistem Ayarları", color: "#F61225" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  <span className="text-white">{item.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#64748B]" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
